import axios from "axios";
import { ICategory, ILegalReference } from "@/store/types/category-types";
import {
  ICapacityPermissions,
  ICapacityState,
  IDashboardDate,
  IDashboardRequestInfo,
  IEmailFeedback,
  IFeedback,
  IFilterCapacity,
  IPartAddress,
  IPartState,
  IPermissionsProfile,
  IProfileCreate,
  IRequestPageCategory,
  IRequestPagePermissions,
  IRequestPageProfile,
  ISubjectExport,
  IToggleCategory,
  IUnityExport
} from "@/store/types/control-types";
import { ApiException } from "./ApiExceptions";

// Request once to define all capacities in the app
export const getCapacities = ({
  part_id,
  capacity_id,
  all = false
}: IFilterCapacity) =>
  axios({
    url: "control/capacity/public",
    method: "GET",
    params: {
      part_id,
      capacity_id,
      all
    }
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const getHierarchyCapacities = () =>
  axios({
    url: "control/capacity/hierarchy",
    method: "GET"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const getPartControl = () =>
  axios({
    url: "control/part/control",
    method: "GET"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const getCapacityControl = (part_id: number) =>
  axios({
    url: `control/capacity/control`,
    method: "GET",
    params: {
      part_id
    }
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const setCapacityPermission = ({
  capacityId,
  permissions
}: ICapacityPermissions) =>
  axios({
    url: `control/capacity/edit-permission`,
    method: "PUT",
    data: {
      capacities: capacityId,
      permissions
    }
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const setPartActive = (list: IPartState[]) =>
  axios({
    url: "control/part/activate",
    method: "PATCH",
    data: list
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const setCapacityActive = (list: ICapacityState[]) =>
  axios({
    url: "control/capacity/activate",
    method: "PATCH",
    data: list
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const getCapacitiesWithProcess = () =>
  axios({
    url: "control/capacity/list-with-process",
    method: "GET"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const getCapacityChildren = (capacityId: number) =>
  axios({
    url: `control/capacity/children/${capacityId}`,
    method: "GET"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

// Request when needs a list of Profiles
export const getAllProfiles = ({
  page,
  paginator_count,
  search,
  permissions,
  profile_assigner,
  part,
  simple,
  list
}: IRequestPageProfile) =>
  axios({
    url: "control/profile",
    method: "GET",
    params: {
      page,
      paginator_count,
      search,
      permissions: JSON.stringify(permissions),
      profile_assigner: JSON.stringify(profile_assigner),
      part,
      simple,
      list
    }
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const getProfile = (profileId: number) =>
  axios({
    url: `control/profile/${profileId}`,
    method: "GET"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const getAllPermissions = ({
  page,
  order_by,
  paginator_count = 10,
  search_text,
  simple
}: IRequestPagePermissions) =>
  axios({
    url: "control/permissions/list",
    method: "GET",
    params: {
      page,
      order_by,
      paginator_count,
      search_text,
      simple
    }
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const getPermission = (permissionId: number) =>
  axios({
    url: `control/permissions/${permissionId}`,
    method: "GET"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const getAllPermissionsKeywords = () =>
  axios({
    url: `control/permissions/keywords`,
    method: "GET"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const editPermission = (
  permissionId: number,
  data: IPermissionsProfile
) =>
  axios({
    url: `control/permissions/edit/${permissionId}`,
    method: "PATCH",
    data
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const createProfile = ({
  name,
  parts,
  all_parts,
  permissions,
  keywords,
  profiles_can_assign
}: IProfileCreate) =>
  axios({
    url: "control/profile/create",
    method: "POST",
    data: { name, parts, all_parts, permissions, keywords, profiles_can_assign }
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const updateProfile = ({
  name,
  parts,
  all_parts,
  permissions,
  keywords,
  profile_x_permission,
  profiles_can_assign,
  profileId
}: IProfileCreate) =>
  axios({
    url: `control/profile/edit/${profileId}`,
    method: "PATCH",
    data: {
      id: profileId,
      name,
      parts,
      all_parts,
      permissions,
      keywords,
      profiles_can_assign
    },
    params: { profile_x_permission }
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const deleteProfile = (profileId: number) =>
  axios({
    url: `control/profile/delete/${profileId}`,
    method: "DELETE"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const activeProfile = (profileId: number, active: boolean) =>
  axios({
    url: `control/profile/active/${profileId}`,
    method: "POST",
    data: { active }
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const getProfileKeywords = (keyword: string) =>
  axios({
    url: `control/profile/list/keywords`,
    method: "GET",
    params: { keyword }
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

// Request when only need the Organizations in the app
export const getAllParts = () =>
  axios({
    url: "control/part/public",
    method: "GET"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const getAllPartsDistinct = () =>
  axios({
    url: "control/part/list",
    method: "GET"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const getAllPartAddresses = () =>
  axios({
    url: "control/part/address",
    method: "GET"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const getPartAddress = (partId: number) =>
  axios({
    url: `control/part/address/${partId}`,
    method: "GET"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const getPartAddressBySeo = (partId: number) =>
  axios({
    url: `control/part/address-seo/${partId}`,
    method: "GET"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const createPartAddress = (data: IPartAddress) =>
  axios({
    url: "control/part/address/create",
    method: "POST",
    data: data
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const updatePartAddress = (partId: number, data: IPartAddress) =>
  axios({
    url: `control/part/address/edit/${partId}`,
    method: "PATCH",
    data: data
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const deletePartAddress = (partId: number) =>
  axios({
    url: `control/part/address/delete/${partId}`,
    method: "DELETE"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

// Request when need the subjects process
export const getSearchSubjects = (search_text: string) =>
  axios({
    url: "control/subject/search",
    method: "GET",
    params: { search_text }
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const getSearchPublicSubjects = (search_text: string) =>
  axios({
    url: "control/subject/search/public",
    method: "GET",
    params: { search_text }
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const getSearchSubjectsById = (subjectId: string) =>
  axios({
    url: `control/subject/${subjectId}`,
    method: "GET"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

// Request when need the legal reference process
export const getAllLegalReference = () =>
  axios({
    url: "control/subject/category/legal-reference",
    method: "GET"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

// Request when need the category
export const getAllCategories = (params: IRequestPageCategory) =>
  axios({
    url: "control/subject/category",
    method: "GET",
    params
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const getCategoryById = (categoryId: string) =>
  axios({
    url: `control/subject/category/${categoryId}`,
    method: "GET"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const deleteCategory = (categoryId: string) =>
  axios({
    url: `/control/subject/category/delete/${categoryId}`,
    method: "DELETE"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

// Send file subject
export const postUploadSubject = (file: FormData) =>
  axios({
    url: "control/subject/upload",
    method: "POST",
    data: file
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

// Request link download file subject
export const requestLinkDownloadSubject = (filters: ISubjectExport) =>
  axios({
    url: "control/subject/export",
    method: "POST",
    data: filters
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

// Delete link download file subject
export const deleteFileSubject = (url: string) =>
  axios({
    url: "control/subject/delete/temp_spreadsheet",
    method: "DELETE",
    data: { url }
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

// Request link download file unity
export const requestLinkDownloadUnity = (filters: IUnityExport) =>
  axios({
    url: "control/capacity/download-sheet",
    method: "POST",
    data: filters
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

// Request when need the indexer
export const getAllIndexer = () =>
  axios({
    url: "control/subject/category/indexer",
    method: "GET"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });
export const postIndexer = (name: string) =>
  axios({
    url: "control/subject/category/indexer/create",
    method: "POST",
    data: { name }
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

// Create new category
export const createCategory = (newCategory: ICategory, createSubject = false) =>
  axios({
    url: `control/subject/category/create`,
    method: "POST",
    params: { create_subject: createSubject },
    data: newCategory
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

// Update exist category
export const updateCategory = (
  category: ICategory,
  id: string,
  createSubject: boolean
) =>
  axios({
    url: "control/subject/category/patch/" + id,
    params: { create_subject: createSubject },
    method: "PATCH",
    data: category
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

// Toggle active or deactive category
export const toggleCategory = (category: IToggleCategory, id: string) =>
  axios({
    url: "control/subject/category/toggle/" + id,
    method: "POST",
    data: category
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

// Request to Get a list of file Types
export const getFileTypes = () =>
  axios({
    url: `process/file/type/list`,
    method: "GET"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

// Request to Get a especific file Types
export const getFileTypeById = (fileTypeId: string) =>
  axios({
    url: `process/file/type/${fileTypeId}`,
    method: "GET"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

// Request to Get a list holidays
export const getHolidays = () =>
  axios({
    url: `control/holidays/${new Date().getFullYear()}`,
    method: "GET"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const getDashboardInfo = (date: IDashboardRequestInfo) =>
  axios({
    url: `dashboard/info-count`,
    params: {
      ...date
    },
    method: "GET"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const getDashboardCapacityInfo = (date: IDashboardRequestInfo) =>
  axios({
    url: `dashboard/capacity/info-count`,
    params: {
      ...date
    },
    method: "GET"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const getDashboardProductivity = (date: IDashboardRequestInfo) =>
  axios({
    url: `dashboard/productivity`,
    params: {
      ...date
    },
    method: "GET"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const getDashboardCapacities = (date: IDashboardRequestInfo) =>
  axios({
    url: `dashboard/capacities`,
    params: {
      ...date
    },
    method: "GET"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const getDashboardDueDate = (date: IDashboardRequestInfo) =>
  axios({
    url: `dashboard/due-date-info`,
    params: {
      ...date
    },
    method: "GET"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const getDueDateList = (date: IDashboardDate) =>
  axios({
    url: `dashboard/date`,
    params: {
      ...date
    },
    method: "GET"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const getDashboardProcessAnalises = (date: IDashboardRequestInfo) =>
  axios({
    url: `dashboard/process-analises`,
    params: {
      ...date
    },
    method: "GET"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const getDashboardSubject = (date: IDashboardRequestInfo) =>
  axios({
    url: `dashboard/subjects`,
    params: {
      ...date
    },
    method: "GET"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const getDashboardCapacityProcessAnalises = (
  date: IDashboardRequestInfo
) =>
  axios({
    url: `dashboard/capacity/process-analises`,
    params: {
      ...date
    },
    method: "GET"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const getDashboardCapacitySubject = (date: IDashboardRequestInfo) =>
  axios({
    url: `dashboard/capacity/subjects`,
    params: {
      ...date
    },
    method: "GET"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const getDashboardCapacityCapacities = (date: IDashboardRequestInfo) =>
  axios({
    url: `dashboard/capacity/capacities`,
    params: {
      ...date
    },
    method: "GET"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const getDashboardCapacityDueDate = (date: IDashboardRequestInfo) =>
  axios({
    url: `dashboard/capacity/due-date-info`,
    params: {
      ...date
    },
    method: "GET"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const getDashboardCapacityProductivity = (date: IDashboardRequestInfo) =>
  axios({
    url: `dashboard/capacity/productivity`,
    params: {
      ...date
    },
    method: "GET"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const getDueDateCapacityList = (date: IDashboardDate) =>
  axios({
    url: `dashboard/capacity/date`,
    params: {
      ...date
    },
    method: "GET"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const getDashboardSignature = ({
  sector = false,
  with_children = false
}: IDashboardRequestInfo) =>
  axios({
    url: `dashboard/signatures`,
    params: {
      sector,
      with_children
    },
    method: "GET"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const postFeedback = ({ type, area, description, url }: IFeedback) =>
  axios({
    url: `control/feedback`,
    data: {
      type,
      area,
      description,
      url
    },
    method: "POST"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const postFeedbackFile = (data: FormData) =>
  axios({
    url: `control/feedback/file`,
    data,
    method: "POST"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const postFeedbackMail = (data: IEmailFeedback) =>
  axios({
    url: `control/feedback/mail`,
    data,
    method: "POST"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const getSearchSupplier = ({
  cnpj,
  name
}: {
  cnpj: string;
  name: string;
}) =>
  axios({
    url: "control/search/supplier",
    method: "GET",
    params: { cnpj, name }
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const getNews = () =>
  axios({
    url: `control/news/list`,
    method: "GET"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const getLaw = (gov_digital = false) =>
  axios({
    url: `control/law/list`,
    method: "GET",
    params: { gov_digital }
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const getDoc = () =>
  axios({
    url: `control/doc/list`,
    method: "GET"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const getChangelog = ({
  paginator_count,
  page
}: {
  paginator_count: number;
  page: number;
}) =>
  axios({
    url: `control/changelog/`,
    method: "GET",
    params: { paginator_count, page }
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const getSuiteNumbers = () =>
  axios({
    url: `control/suite-numbers`,
    method: "GET"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

export const getReportTypes = () =>
  axios({
    url: `/report/types`,
    method: "GET"
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });

// Create legal reference
export const createLegalReference = (legalReference: ILegalReference) =>
  axios({
    url: "control/subject/category/legal-reference/create",
    method: "POST",
    data: legalReference
  })
    .then(response => {
      return { kind: "ok", data: response.data };
    })
    .catch(error => {
      return ApiException(error.response);
    });
